'use client';

import { Key, useCallback } from 'react';
import { FrontendBroker, FrontendLoad } from '@trucksmarter/shared';
import {
  DallasTable,
  DallasTableHeaderCell,
  DallasTableHeaderRow,
  DallasTableVirtualRow,
  RowTemplateProps,
} from '@ui/Dallas/Table/DallasTable';
import clsx from 'clsx';
import AgeCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/AgeCell';
import BrokerCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/BrokerCell';
import AppointmentCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/AppointmentCell';
import DistanceCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/DistanceCell';
import AddressCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/AddressCell';
import StopListHovercardAnchorCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/StopListHovercardAnchorCell';
import LoadTableTripArrowWithExtraStopCount from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/LoadTableHoverableStopListAnchor/LoadTableTripArrowWithExtraStopCount';
import TrailerTypesCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/TrailerTypesCell';
import DimensionsCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/DimensionsCell';
import ViewPublicLoadCell from './ViewPublicLoadCell';
import PublicLoadTableResponsiveRowContent from './PublicLoadTableResponsiveRowContent';
import EquipmentCompactCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/EquipmentCompactCell';
import LoadTableStopListSummary from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/LoadTableHoverableStopListAnchor/LoadTableStopListSummary';
import BrokerAndPickupCell from '../../../../../../(authenticated)/(carrier)/(marketplace)/(loads)/loadboard/_ui/LoadTableCells/BrokerAndPickupCell';

import styles from './PublicLoadTable.module.scss';

function GET_LOAD_KEY(load: FrontendLoad): Key {
  return load.id;
}

export interface Props {
  className?: string;
  loads: FrontendLoad[];
  broker: FrontendBroker;
  onLoadClick?: (load: FrontendLoad) => void;
}

export default function PublicLoadTable({
  className,
  loads,
  broker,
  onLoadClick,
}: Props) {
  const rowTemplate = useCallback(
    ({ dataItem: load, last: isLast }: RowTemplateProps<FrontendLoad>) => (
      <PublicLoadTableRow
        load={load}
        broker={broker}
        last={isLast}
        onClick={onLoadClick ? () => onLoadClick(load) : undefined}
      />
    ),
    [broker, onLoadClick],
  );

  return (
    <div className={clsx(className, styles.root)}>
      <DallasTable
        className={styles.table}
        headerRow={
          <DallasTableHeaderRow>
            <PublicLoadTableResponsiveRowContent
              ageColumn={
                <DallasTableHeaderCell
                  className={styles.ageColumn}
                  columnName="Age"
                />
              }
              brokerColumn={
                <DallasTableHeaderCell
                  className={styles.brokerColumn}
                  columnName="Broker"
                />
              }
              brokerAndPickupColumn={
                <DallasTableHeaderCell
                  className={styles.brokerColumn}
                  columnName="Broker | Pickup"
                />
              }
              distanceColumn={
                <DallasTableHeaderCell
                  className={styles.distanceColumn}
                  columnName="Distance"
                />
              }
              pickupAppointmentColumn={
                <DallasTableHeaderCell
                  className={styles.appointmentColumn}
                  columnName="Pickup"
                />
              }
              pickupAddressColumn={<th className={styles.addressColumn} />}
              stopsColumn={<th className={styles.stopsColumn} />}
              tripArrowAndExtraStopsColumn={
                <th className={styles.tripArrowAndExtraStopsColumn} />
              }
              deliveryAddressColumn={
                <DallasTableHeaderCell
                  className={styles.addressColumn}
                  columnName="Delivery"
                />
              }
              deliveryAppointmentNextToAddressColumn={
                <th className={styles.appointmentColumn} />
              }
              deliveryAppointmentColumn={
                <DallasTableHeaderCell
                  className={styles.appointmentColumn}
                  columnName="Delivery"
                />
              }
              equipmentCompactColumn={
                <DallasTableHeaderCell
                  className={styles.equipmentCompactColumn}
                  columnName="Equipment"
                />
              }
              trailerTypeColumn={
                <DallasTableHeaderCell
                  className={styles.trailerTypesColumn}
                  columnName="Equipment"
                />
              }
              dimensionsColumn={<th className={styles.dimensionsColumn} />}
              callToActionColumn={
                <DallasTableHeaderCell
                  className={styles.actionColumn}
                  columnName="Action"
                />
              }
              availabilityColumn={<th />}
            />
          </DallasTableHeaderRow>
        }
        data={loads}
        getDataItemKey={GET_LOAD_KEY}
        rowTemplate={rowTemplate}
      />
    </div>
  );
}

interface PublicLoadTableRowProps {
  load: FrontendLoad;
  broker: FrontendBroker;
  last: boolean;
  onClick?: () => void;
}

function PublicLoadTableRow({
  load,
  broker,
  last: isLast,
  onClick,
}: PublicLoadTableRowProps) {
  return (
    <DallasTableVirtualRow last={isLast} onClick={onClick}>
      <PublicLoadTableResponsiveRowContent
        ageColumn={<AgeCell className={styles.ageColumn} load={load} />}
        brokerColumn={
          <BrokerCell className={styles.brokerColumn} broker={broker} />
        }
        brokerAndPickupColumn={
          <BrokerAndPickupCell
            className={styles.brokerColumn}
            broker={broker}
            location={load.pickup}
          />
        }
        distanceColumn={
          <DistanceCell className={styles.distanceColumn} load={load} />
        }
        pickupAppointmentColumn={
          <AppointmentCell
            className={styles.appointmentColumn}
            location={load.pickup}
            brokerId={load.broker}
          />
        }
        pickupAddressColumn={
          <AddressCell
            className={styles.addressColumn}
            address={load.pickup.address}
          />
        }
        tripArrowAndExtraStopsColumn={
          <StopListHovercardAnchorCell
            className={styles.tripArrowAndExtraStopsColumn}
            load={load}
            brokerId={load.broker}
          >
            <LoadTableTripArrowWithExtraStopCount stops={load.stops} />
          </StopListHovercardAnchorCell>
        }
        stopsColumn={
          <StopListHovercardAnchorCell
            className={styles.stopsColumn}
            load={load}
            brokerId={load.broker}
          >
            <LoadTableStopListSummary stops={load.stops} />
          </StopListHovercardAnchorCell>
        }
        deliveryAddressColumn={
          <AddressCell
            className={styles.addressColumn}
            address={load.delivery.address}
          />
        }
        deliveryAppointmentNextToAddressColumn={
          <AppointmentCell
            className={styles.appointmentColumn}
            location={load.delivery}
            brokerId={load.broker}
          />
        }
        deliveryAppointmentColumn={
          <AppointmentCell
            className={styles.appointmentColumn}
            location={load.delivery}
            brokerId={load.broker}
          />
        }
        equipmentCompactColumn={
          <EquipmentCompactCell
            className={styles.equipmentCompactColumn}
            load={load}
          />
        }
        trailerTypeColumn={
          <TrailerTypesCell
            className={styles.trailerTypesColumn}
            trailerTypes={load.equipment.trailerTypes}
          />
        }
        dimensionsColumn={
          <DimensionsCell className={styles.dimensionsColumn} load={load} />
        }
        callToActionColumn={
          <ViewPublicLoadCell className={styles.actionCell} loadId={load.id} />
        }
        availabilityColumn={<td />}
      />
    </DallasTableVirtualRow>
  );
}
